import React from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Users, CalendarCheck, Leaf, TrendingUp } from "lucide-react";

const STATUS_LABEL = {
  NEW: "Nuovi", TO_CONTACT: "Da contattare", CONTACTED: "Contattati", QUALIFIED: "Qualificati",
  APPOINTMENT: "Appuntamento", TEST_DRIVE: "Prova", QUOTATION: "Preventivo", ORDER: "Ordine", WON: "Vinti", LOST: "Persi",
};

const Kpi = ({ label, value, icon: Icon, accent }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold text-slate-500">{label}</span>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: accent || "var(--sc-emerald)" }}><Icon size={18} /></span>
    </div>
    <p className="mt-2 text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>{value}</p>
  </div>
);

export default function Dashboard() {
  const { data = {}, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get("/admin/dashboard").then((r) => r.data) });
  const maxFunnel = Math.max(1, ...Object.values(data.funnel || {}));

  return (
    <AdminLayout>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Dashboard</h1>
      <p className="text-slate-500 mb-6">Panoramica lead e conversioni organiche.</p>

      {isLoading ? <p className="text-slate-500">Caricamento...</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Lead totali" value={data.total ?? 0} icon={Users} />
            <Kpi label="Lead oggi" value={data.today ?? 0} icon={TrendingUp} accent="var(--sc-amber)" />
            <Kpi label="Lead organici" value={data.organic ?? 0} icon={Leaf} />
            <Kpi label="Richieste prova" value={data.test_drives ?? 0} icon={CalendarCheck} accent="var(--sc-amber)" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <Kpi label="Settimana" value={data.this_week ?? 0} icon={TrendingUp} />
            <Kpi label="Mese" value={data.this_month ?? 0} icon={TrendingUp} />
            <Kpi label="Vinti" value={data.won ?? 0} icon={TrendingUp} />
            <Kpi label="Ricavi" value={(data.revenue || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })} icon={TrendingUp} accent="var(--sc-amber)" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <h2 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Funnel lead</h2>
              <div className="space-y-2.5">
                {Object.entries(data.funnel || {}).map(([s, c]) => (
                  <div key={s} className="flex items-center gap-3">
                    <span className="w-28 text-sm text-slate-600 shrink-0">{STATUS_LABEL[s] || s}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden"><div className="h-full rounded-full flex items-center justify-end px-2 text-xs font-bold text-white" style={{ width: `${Math.max(6, (c / maxFunnel) * 100)}%`, backgroundColor: "var(--sc-emerald)" }}>{c}</div></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <h2 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Top landing page (organiche)</h2>
              <div className="space-y-2">
                {(data.top_pages || []).length === 0 && <p className="text-slate-400 text-sm">Nessun dato ancora.</p>}
                {(data.top_pages || []).map((p) => (
                  <div key={p.page} className="flex items-center justify-between text-sm border-b border-slate-100 py-2">
                    <span className="text-slate-600 truncate max-w-[70%]">{p.page || "/"}</span>
                    <span className="font-bold text-slate-900">{p.count}</span>
                  </div>
                ))}
              </div>
              <h2 className="font-bold text-slate-900 mt-6 mb-3" style={{ fontFamily: "Outfit" }}>Top province</h2>
              <div className="flex flex-wrap gap-2">
                {(data.top_provinces || []).map((p) => <span key={p.province} className="text-sm bg-emerald-50 text-emerald-900 font-semibold px-3 py-1.5 rounded-full">{p.province} · {p.count}</span>)}
                {(data.top_provinces || []).length === 0 && <p className="text-slate-400 text-sm">Nessun dato ancora.</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
